import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../utils/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [demoStatus, setDemoStatus] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
          setToken(storedToken);
          
          // If it's a demo user, get demo status
          if (response.data.is_demo) {
            try {
              const demoRes = await api.get("/demo/status");
              setDemoStatus(demoRes.data);
            } catch (e) {
              console.error("Failed to get demo status:", e);
            }
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.post("/auth/login", { email, password, remember_me: rememberMe });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referralCode = null, betaToken = null, discountLinkSlug = null) => {
    const payload = { email, password, name };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    if (betaToken) {
      payload.beta_token = betaToken;
    }
    if (discountLinkSlug) {
      payload.discount_link_slug = discountLinkSlug;
    }
    const response = await api.post("/auth/register", payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return { access_token, user: userData };
  };
  
  const startDemo = async () => {
    const response = await api.post("/demo/start");
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, is_demo: true });
    setDemoStatus({
      is_demo: true,
      minutes_remaining: response.data.expires_in_minutes,
      session_id: response.data.session_id
    });
    return userData;
  };
  
  const endDemo = useCallback(async () => {
    if (user?.is_demo) {
      try {
        await api.post("/demo/end");
      } catch (e) {
        console.error("Failed to end demo:", e);
      }
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setDemoStatus(null);
  }, [user]);

  const logout = () => {
    if (user?.is_demo) {
      endDemo();
    } else {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      // Ask the service worker to clear all qs-* caches so the next user
      // on this device doesn't see the previous user's cached
      // customers/quotes/certs (Sprint 2 offline caching).
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        try {
          navigator.serviceWorker.controller?.postMessage({ type: "CLEAR_CACHES" });
        } catch (_e) { /* non-fatal */ }
      }
    }
  };

  const updateUser = async (data) => {
    const response = await api.put("/auth/me", data);
    setUser(response.data);
    return response.data;
  };

  // Re-fetch the current user record. Used after billing changes (e.g.
  // adding a card via the PAST_DUE banner) so the UI immediately
  // reflects the new `subscription_status` / `has_square_subscription`
  // flags and the banner disappears without a manual refresh.
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (e) {
      console.error("Failed to refresh user:", e);
      return null;
    }
  }, []);
  
  const refreshDemoStatus = useCallback(async () => {
    if (user?.is_demo) {
      try {
        const response = await api.get("/demo/status");
        setDemoStatus(response.data);
        return response.data;
      } catch (e) {
        console.error("Failed to refresh demo status:", e);
      }
    }
    return null;
  }, [user]);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    startDemo,
    endDemo,
    demoStatus,
    refreshDemoStatus,
    isAuthenticated: !!token,
    isAdmin: user?.role === "admin",
    isDemo: user?.is_demo || false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
